import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { 
  Brain, Check, X, ChevronRight, ChevronDown, Clock, BarChart3, 
  Target, Smartphone, Star, Zap, BookOpen, Trophy, Users, 
  Sparkles, ArrowRight, Play, Shield, Flame, Award, 
  Stethoscope, Building2, Scale, GraduationCap, TrendingUp,
  Settings, DollarSign, Calculator, Lock, Package, MessageSquare
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSelector from '../components/LanguageSelector';

// TuPlazaIA Premium EdTech Color Palette (Duolingo-inspired)
const colors = {
  // Primary greens (like Duolingo)
  primary: '#58CC02',        // Vibrant Green
  primaryDark: '#46A302',    // Darker Green
  primaryLight: '#89E219',   // Light Green
  
  // Secondary colors
  blue: '#1CB0F6',           // Sky Blue
  orange: '#FF9600',         // Warm Orange
  red: '#FF4B4B',            // Coral Red
  purple: '#CE82FF',         // Soft Purple
  pink: '#FF86D0',           // Pink
  yellow: '#FFC800',         // Golden Yellow
  
  // Neutrals
  dark: '#3C3C3C',           // Dark Gray
  gray: '#777777',           // Medium Gray
  lightGray: '#E5E5E5',      // Light Gray
  background: '#F7F7F7',     // Off-white
  white: '#FFFFFF',
  
  // Gradients
  greenGradient: 'linear-gradient(135deg, #58CC02 0%, #89E219 100%)',
  blueGradient: 'linear-gradient(135deg, #1CB0F6 0%, #49D0FF 100%)',
  purpleGradient: 'linear-gradient(135deg, #A560E8 0%, #CE82FF 100%)',
};

// Premium TuPlazaIA Logo Component (SVG)
const OposaiLogo = ({ size = 48, showText = true, className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Background circle */}
      <circle cx="32" cy="32" r="30" fill="url(#logoGradient)" />
      
      {/* Graduation cap base */}
      <path d="M32 16L8 28L32 40L56 28L32 16Z" fill="#FFFFFF" />
      
      {/* Cap top */}
      <path d="M32 16L8 28L16 32V42C16 42 24 48 32 48C40 48 48 42 48 42V32L56 28L32 16Z" fill="#FFFFFF" opacity="0.9"/>
      
      {/* Cap detail */}
      <rect x="16" y="32" width="32" height="12" rx="2" fill="#FFFFFF" opacity="0.8"/>
      
      {/* AI Brain symbol */}
      <circle cx="32" cy="38" r="6" fill="url(#brainGradient)" />
      <path d="M29 36.5C29 36.5 30 35 32 35C34 35 35 36.5 35 36.5" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M30 39H34" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="30" cy="37" r="1" fill="#FFFFFF"/>
      <circle cx="34" cy="37" r="1" fill="#FFFFFF"/>
      
      {/* Tassel */}
      <path d="M48 28L52 32L52 44" stroke="#FFC800" strokeWidth="3" strokeLinecap="round"/>
      <circle cx="52" cy="46" r="3" fill="#FFC800"/>
      
      {/* Sparkle effects */}
      <circle cx="18" cy="20" r="2" fill="#FFFFFF" opacity="0.8"/>
      <circle cx="46" cy="18" r="1.5" fill="#FFFFFF" opacity="0.6"/>
      <circle cx="12" cy="34" r="1" fill="#FFFFFF" opacity="0.5"/>
      
      {/* Gradients definitions */}
      <defs>
        <linearGradient id="logoGradient" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop stopColor="#58CC02"/>
          <stop offset="1" stopColor="#46A302"/>
        </linearGradient>
        <linearGradient id="brainGradient" x1="26" y1="32" x2="38" y2="44" gradientUnits="userSpaceOnUse">
          <stop stopColor="#1CB0F6"/>
          <stop offset="1" stopColor="#A560E8"/>
        </linearGradient>
      </defs>
    </svg>
    {showText && (
      <span className="text-2xl font-extrabold tracking-tight" style={{ color: colors.primary }}>
        TuPlaza<span style={{ color: colors.blue }}>IA</span>
      </span>
    )}
  </div>
);

// Mascot Component - Friendly Iberian Lynx with graduation cap
const TuPlazaIAMascot = ({ size = 200, expression = "happy" }) => (
  <svg width={size} height={size} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Body */}
    <ellipse cx="100" cy="145" rx="55" ry="45" fill="url(#mascotBody)"/>
    
    {/* Belly/Chest fur */}
    <ellipse cx="100" cy="150" rx="35" ry="30" fill="#FFFFFF" opacity="0.95"/>
    
    {/* Head */}
    <ellipse cx="100" cy="80" rx="50" ry="45" fill="url(#mascotBody)"/>
    
    {/* Face lighter area */}
    <ellipse cx="100" cy="90" rx="38" ry="32" fill="url(#faceGradient)"/>
    
    {/* Lynx ear tufts (left) */}
    <path d="M55 35L45 15L60 30Z" fill="#3C3C3C"/>
    <path d="M58 38L50 22L62 33Z" fill="url(#mascotBody)"/>
    
    {/* Lynx ear tufts (right) */}
    <path d="M145 35L155 15L140 30Z" fill="#3C3C3C"/>
    <path d="M142 38L150 22L138 33Z" fill="url(#mascotBody)"/>
    
    {/* Inner ears */}
    <path d="M60 45L52 28L68 40Z" fill="#FFB6C1"/>
    <path d="M140 45L148 28L132 40Z" fill="#FFB6C1"/>
    
    {/* Lynx beard/whisker tufts */}
    <path d="M50 95C45 100 42 110 48 115C54 110 52 100 50 95Z" fill="url(#mascotBody)"/>
    <path d="M150 95C155 100 158 110 152 115C146 110 148 100 150 95Z" fill="url(#mascotBody)"/>
    
    {/* Eyes */}
    <ellipse cx="78" cy="78" rx="14" ry="16" fill="#FFFFFF"/>
    <ellipse cx="122" cy="78" rx="14" ry="16" fill="#FFFFFF"/>
    <ellipse cx="78" cy="80" rx="9" ry="11" fill="#FFB800"/>
    <ellipse cx="122" cy="80" rx="9" ry="11" fill="#FFB800"/>
    <ellipse cx="78" cy="80" rx="5" ry="7" fill="#3C3C3C"/>
    <ellipse cx="122" cy="80" rx="5" ry="7" fill="#3C3C3C"/>
    <circle cx="81" cy="76" r="3" fill="#FFFFFF"/>
    <circle cx="125" cy="76" r="3" fill="#FFFFFF"/>
    
    {/* Eyebrows for expression */}
    {expression === "happy" && (
      <>
        <path d="M62 65C65 62 72 61 88 65" stroke="#3C3C3C" strokeWidth="2.5" strokeLinecap="round"/>
        <path d="M138 65C135 62 128 61 112 65" stroke="#3C3C3C" strokeWidth="2.5" strokeLinecap="round"/>
      </>
    )}
    
    {/* Nose */}
    <ellipse cx="100" cy="98" rx="8" ry="6" fill="#3C3C3C"/>
    <ellipse cx="100" cy="97" rx="3" ry="2" fill="#666666"/>
    
    {/* Mouth */}
    <path d="M100 104V110" stroke="#3C3C3C" strokeWidth="2" strokeLinecap="round"/>
    <path d="M92 112C96 116 104 116 108 112" stroke="#3C3C3C" strokeWidth="2" strokeLinecap="round"/>
    
    {/* Lynx spots/markings */}
    <circle cx="65" cy="95" r="4" fill="#46A302" opacity="0.4"/>
    <circle cx="135" cy="95" r="4" fill="#46A302" opacity="0.4"/>
    <circle cx="75" cy="110" r="3" fill="#46A302" opacity="0.3"/>
    <circle cx="125" cy="110" r="3" fill="#46A302" opacity="0.3"/>
    
    {/* Blush */}
    <ellipse cx="62" cy="95" rx="8" ry="5" fill="#FF86D0" opacity="0.4"/>
    <ellipse cx="138" cy="95" rx="8" ry="5" fill="#FF86D0" opacity="0.4"/>
    
    {/* Graduation Cap */}
    <path d="M100 20L40 48L100 76L160 48L100 20Z" fill="#3C3C3C"/>
    <rect x="60" y="48" width="80" height="10" rx="2" fill="#3C3C3C"/>
    <path d="M150 48L162 54V76" stroke="#FFC800" strokeWidth="4" strokeLinecap="round"/>
    <circle cx="162" cy="80" r="6" fill="#FFC800"/>
    
    {/* Arms/Paws */}
    <ellipse cx="50" cy="140" rx="18" ry="25" fill="url(#mascotBody)" transform="rotate(-15 50 140)"/>
    <ellipse cx="150" cy="140" rx="18" ry="25" fill="url(#mascotBody)" transform="rotate(15 150 140)"/>
    
    {/* Paw pads */}
    <ellipse cx="45" cy="158" rx="8" ry="6" fill="#FFB6C1"/>
    <ellipse cx="155" cy="158" rx="8" ry="6" fill="#FFB6C1"/>
    
    {/* Feet */}
    <ellipse cx="75" cy="185" rx="18" ry="10" fill="url(#mascotBody)"/>
    <ellipse cx="125" cy="185" rx="18" ry="10" fill="url(#mascotBody)"/>
    <ellipse cx="75" cy="188" rx="10" ry="5" fill="#FFB6C1"/>
    <ellipse cx="125" cy="188" rx="10" ry="5" fill="#FFB6C1"/>
    
    {/* Tail */}
    <ellipse cx="160" cy="165" rx="25" ry="12" fill="url(#mascotBody)" transform="rotate(-20 160 165)"/>
    <ellipse cx="175" cy="160" rx="10" ry="8" fill="#3C3C3C"/>
    
    {/* Book in paw */}
    <rect x="145" y="125" width="28" height="20" rx="3" fill="#1CB0F6" transform="rotate(8 145 125)"/>
    <rect x="148" y="128" width="22" height="14" rx="2" fill="#FFFFFF" transform="rotate(8 148 128)"/>
    <path d="M152 132L166 134" stroke="#1CB0F6" strokeWidth="2" transform="rotate(8 152 132)"/>
    <path d="M152 137L162 138" stroke="#1CB0F6" strokeWidth="1.5" transform="rotate(8 152 137)"/>
    
    <defs>
      <linearGradient id="mascotBody" x1="50" y1="30" x2="150" y2="190" gradientUnits="userSpaceOnUse">
        <stop stopColor="#58CC02"/>
        <stop offset="1" stopColor="#46A302"/>
      </linearGradient>
      <linearGradient id="faceGradient" x1="62" y1="60" x2="138" y2="120" gradientUnits="userSpaceOnUse">
        <stop stopColor="#89E219"/>
        <stop offset="1" stopColor="#6BBF00"/>
      </linearGradient>
    </defs>
  </svg>
);

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, color, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true }}
    className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 hover:border-gray-200 hover:shadow-xl transition-all duration-300 group"
  >
    <div 
      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
      style={{ backgroundColor: `${color}20` }}
    >
      <Icon className="w-7 h-7" style={{ color }} />
    </div>
    <h3 className="text-lg font-bold mb-2" style={{ color: colors.dark }}>{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </motion.div>
);

// Category Card Component
const CategoryCard = ({ emoji, name, description, count, delay = 0 }) => {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay }}
      viewport={{ once: true }}
      onClick={() => navigate('/auth?mode=register')}
      className="bg-white rounded-2xl p-5 shadow-md border-2 border-gray-100 hover:border-green-300 hover:shadow-lg transition-all cursor-pointer group"
    >
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl" style={{ backgroundColor: `${colors.primary}15` }}>
          {emoji}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-800">{name}</h3>
            <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: colors.primary, color: 'white' }}>
              {count}+
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-green-500 transition-colors" />
      </div>
    </motion.div>
  );
};

// Stats Counter Component
const StatCounter = ({ value, label, icon: Icon, color }) => (
  <div className="text-center">
    <div className="flex items-center justify-center gap-2 mb-1">
      <Icon className="w-5 h-5" style={{ color }} />
      <span className="text-3xl font-extrabold" style={{ color }}>{value}</span>
    </div>
    <p className="text-sm text-gray-600 font-medium">{label}</p>
  </div>
);

// Lead Capture Modal for B2B Calculator
const LeadCaptureModal = ({ isOpen, onClose, calculatorData, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    licenses: calculatorData?.licenses || 50,
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/leads/institutional`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          calculator_data: calculatorData,
          source: 'landing_calculator',
          lead_type: 'institutional'
        })
      });
      
      setSubmitted(true);
      if (onSuccess) onSuccess(formData.email);
    } catch (error) {
      console.error('Error submitting lead:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-900 rounded-2xl max-w-lg w-full p-6 border border-slate-700"
      >
        {!submitted ? (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-indigo-500/20 flex items-center justify-center mx-auto mb-4">
                <Calculator className="w-8 h-8 text-indigo-400" />
              </div>
              <h3 className="text-2xl font-bold text-white">Recibe tu presupuesto personalizado</h3>
              <p className="text-slate-400 mt-2">
                Te enviaremos los precios por licencia adaptados a tu volumen de alumnos
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Nombre *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full p-3 rounded-lg bg-slate-800 border border-slate-600 text-white"
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Teléfono</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full p-3 rounded-lg bg-slate-800 border border-slate-600 text-white"
                    placeholder="+34 600..."
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-slate-300 mb-1 block">Email corporativo *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full p-3 rounded-lg bg-slate-800 border border-slate-600 text-white"
                  placeholder="tu@academia.com"
                />
              </div>

              <div>
                <label className="text-sm text-slate-300 mb-1 block">Nombre de la academia *</label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="w-full p-3 rounded-lg bg-slate-800 border border-slate-600 text-white"
                  placeholder="Academia Ejemplo"
                />
              </div>

              <div>
                <label className="text-sm text-slate-300 mb-1 block">Mensaje (opcional)</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full p-3 rounded-lg bg-slate-800 border border-slate-600 text-white resize-none"
                  rows={2}
                  placeholder="¿Qué oposiciones preparáis?"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  onClick={onClose}
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                >
                  {submitting ? 'Enviando...' : 'Recibir presupuesto'}
                </Button>
              </div>
            </form>

            <p className="text-xs text-slate-500 text-center mt-4">
              Al enviar, aceptas recibir información comercial de TuPlazaIA
            </p>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-emerald-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">¡Solicitud enviada!</h3>
            <p className="text-slate-400 mb-6">
              En menos de 24h recibirás en <strong className="text-white">{formData.email}</strong>:
            </p>
            <ul className="text-left space-y-2 mb-6">
              <li className="flex items-center gap-2 text-slate-300">
                <Check className="w-4 h-4 text-emerald-400" />
                Presupuesto para {calculatorData?.licenses || 50} licencias
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <Check className="w-4 h-4 text-emerald-400" />
                Precios por volumen con descuentos
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <Check className="w-4 h-4 text-emerald-400" />
                Opciones de créditos IA para tus alumnos
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <Check className="w-4 h-4 text-emerald-400" />
                Demo personalizada de la plataforma
              </li>
            </ul>
            <Button onClick={onClose} className="bg-slate-700 hover:bg-slate-600">
              Cerrar
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// ROI Calculator for Academies - With Lead Capture
const ROICalculator = () => {
  const [licenses, setLicenses] = useState(50);
  const [billingPeriod, setBillingPeriod] = useState('annual'); // 'monthly' or 'annual'
  const [includeAI, setIncludeAI] = useState(true);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [hasUnlockedResults, setHasUnlockedResults] = useState(false);

  // COSTES REALES (por alumno/mes)
  // - Coste IA (GPT + ElevenLabs + Whisper): ~3€/alumno/mes (10 sesiones)
  // - Infraestructura: ~0.50€/alumno/mes
  // - Soporte: ~0.50€/alumno/mes
  // Total coste con IA: ~4€/alumno/mes
  // Total coste sin IA: ~1€/alumno/mes
  
  // PRECIOS BASE (mantienen margen 60-70%)
  const BASE_PRICE_WITH_AI = 29;  // €/licencia/mes - Margen ~86% sobre coste
  const BASE_PRICE_NO_AI = 15;    // €/licencia/mes - Margen ~93% sobre coste
  
  // Pricing tiers based on license count (descuentos por volumen)
  const getPricePerLicense = (numLicenses, withAI, isAnnual) => {
    const basePrice = withAI ? BASE_PRICE_WITH_AI : BASE_PRICE_NO_AI;
    
    let volumeMultiplier = 1;
    if (numLicenses >= 500) volumeMultiplier = 0.60;      // 40% descuento
    else if (numLicenses >= 200) volumeMultiplier = 0.70; // 30% descuento
    else if (numLicenses >= 100) volumeMultiplier = 0.80; // 20% descuento
    else if (numLicenses >= 50) volumeMultiplier = 0.85;  // 15% descuento
    else if (numLicenses >= 20) volumeMultiplier = 0.92;  // 8% descuento
    
    // Descuento adicional por pago anual (2 meses gratis = ~17%)
    const annualMultiplier = isAnnual ? (10/12) : 1;
    
    return basePrice * volumeMultiplier * annualMultiplier;
  };

  const pricePerLicense = getPricePerLicense(licenses, includeAI, billingPeriod === 'annual');
  const monthlyTotal = licenses * pricePerLicense;
  const contractMonths = billingPeriod === 'annual' ? 12 : 1;
  const totalPayment = billingPeriod === 'annual' ? monthlyTotal * 12 : monthlyTotal;
  
  // Calculate discount
  const basePrice = includeAI ? BASE_PRICE_WITH_AI : BASE_PRICE_NO_AI;
  const effectiveDiscount = Math.round((1 - pricePerLicense / basePrice) * 100);
  
  // ROI calculation (más conservador)
  const avgStudentFee = 150; // €/mes promedio que paga un alumno
  const retentionImprovement = 0.12; // 12% mejora en retención
  const passRateImprovement = 0.15; // 15% mejora en tasa de aprobados
  
  const retentionValue = licenses * avgStudentFee * retentionImprovement * 4; // 4 meses extra
  const reputationValue = licenses * passRateImprovement * avgStudentFee * 2;
  const totalBenefit = retentionValue + reputationValue;
  const annualCost = billingPeriod === 'annual' ? totalPayment : monthlyTotal * 12;
  const roi = Math.round(((totalBenefit - annualCost) / annualCost) * 100);

  const calculatorData = {
    licenses,
    billingPeriod,
    includeAI,
    pricePerLicense,
    monthlyTotal,
    totalPayment,
    effectiveDiscount,
    roi
  };

  // License tier info
  const tiers = [
    { min: 1, max: 19, name: 'Starter', discount: '0%' },
    { min: 20, max: 49, name: 'Growth', discount: '8%' },
    { min: 50, max: 99, name: 'Professional', discount: '15%' },
    { min: 100, max: 199, name: 'Business', discount: '20%' },
    { min: 200, max: 499, name: 'Enterprise', discount: '30%' },
    { min: 500, max: 9999, name: 'Custom', discount: '40%' },
  ];

  const currentTier = tiers.find(t => licenses >= t.min && licenses <= t.max) || tiers[tiers.length - 1];

  return (
    <>
      <LeadCaptureModal 
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        calculatorData={calculatorData}
        onSuccess={() => setHasUnlockedResults(true)}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-white mb-2">Precios por Licencia</h3>
          <p className="text-slate-400">Paga solo por los alumnos activos. Sin costes ocultos.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Configuration Panel */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <h4 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configura tu plan
            </h4>

            <div className="space-y-6">
              {/* License slider */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm text-slate-300">Número de licencias</label>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    licenses >= 200 ? 'bg-purple-500 text-white' :
                    licenses >= 100 ? 'bg-indigo-500 text-white' :
                    licenses >= 50 ? 'bg-blue-500 text-white' :
                    'bg-slate-600 text-white'
                  }`}>
                    {currentTier.name} ({currentTier.discount} vol.)
                  </span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="500"
                  step="10"
                  value={licenses}
                  onChange={(e) => setLicenses(parseInt(e.target.value))}
                  className="w-full accent-indigo-500"
                />
                <div className="flex justify-between text-white mt-2">
                  <span className="text-3xl font-black">{licenses}</span>
                  <span className="text-sm text-slate-400 self-end">licencias</span>
                </div>
              </div>

              {/* Billing period */}
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Facturación</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setBillingPeriod('monthly')}
                    className={`py-4 px-4 rounded-xl font-medium transition-all ${
                      billingPeriod === 'monthly'
                        ? 'bg-indigo-500 text-white ring-2 ring-indigo-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20'
                    }`}
                  >
                    <span className="block text-lg font-bold">Mensual</span>
                    <span className="text-xs opacity-70">Flexibilidad total</span>
                  </button>
                  <button
                    onClick={() => setBillingPeriod('annual')}
                    className={`py-4 px-4 rounded-xl font-medium transition-all relative ${
                      billingPeriod === 'annual'
                        ? 'bg-indigo-500 text-white ring-2 ring-indigo-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20'
                    }`}
                  >
                    <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded-full font-bold">
                      2 meses gratis
                    </span>
                    <span className="block text-lg font-bold">Anual</span>
                    <span className="text-xs opacity-70">Pago único</span>
                  </button>
                </div>
              </div>

              {/* AI Toggle */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 rounded-xl border border-purple-400/30">
                <div>
                  <p className="text-white font-bold flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    Tutor IA Incluido
                  </p>
                  <p className="text-xs text-slate-400">GPT-4 + ElevenLabs 24/7</p>
                </div>
                <button
                  onClick={() => setIncludeAI(!includeAI)}
                  className={`w-14 h-8 rounded-full transition-all ${
                    includeAI ? 'bg-purple-500' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full bg-white transition-all ${
                    includeAI ? 'ml-7' : 'ml-1'
                  }`} />
                </button>
              </div>

              {/* Tier breakdown */}
              <div className="bg-black/20 rounded-lg p-4">
                <p className="text-sm text-slate-400 mb-2">Descuentos por volumen:</p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {tiers.slice(0, 6).map((tier) => (
                    <div 
                      key={tier.name}
                      className={`p-2 rounded text-center ${
                        currentTier.name === tier.name 
                          ? 'bg-indigo-500/30 text-indigo-200 ring-1 ring-indigo-400' 
                          : 'bg-white/5 text-slate-500'
                      }`}
                    >
                      <div className="font-bold">{tier.min}+</div>
                      <div className="opacity-70">{tier.discount}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Panel */}
          <div className="space-y-4">
            {/* Price Card */}
            <div 
              className={`bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-6 text-white relative overflow-hidden ${!hasUnlockedResults ? 'cursor-pointer' : ''}`}
              onClick={() => !hasUnlockedResults && setShowLeadModal(true)}
            >
              {!hasUnlockedResults && (
                <div className="absolute inset-0 backdrop-blur-md bg-black/40 flex flex-col items-center justify-center z-10">
                  <Lock className="w-10 h-10 text-white mb-2" />
                  <p className="text-white font-bold text-lg">Ver precios personalizados</p>
                  <p className="text-indigo-200 text-sm">Recibe tu presupuesto por email</p>
                </div>
              )}
              
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-indigo-200 text-sm">Precio por licencia</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-black">
                      {hasUnlockedResults ? pricePerLicense.toFixed(0) : '??'}€
                    </span>
                    <span className="text-indigo-200">/mes</span>
                  </div>
                  {includeAI && (
                    <p className="text-xs text-indigo-300 mt-1">Incluye Tutor IA ilimitado</p>
                  )}
                </div>
                {effectiveDiscount > 0 && (
                  <span className="px-3 py-1 bg-green-500 text-white rounded-full text-sm font-bold">
                    -{effectiveDiscount}%
                  </span>
                )}
              </div>

              <div className="border-t border-white/20 pt-4 mt-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-indigo-200">
                    {billingPeriod === 'annual' ? 'Equivalente mensual' : 'Total mensual'} ({licenses} lic.)
                  </span>
                  <span className="font-bold">{hasUnlockedResults ? monthlyTotal.toFixed(0) : '???'}€</span>
                </div>
                {billingPeriod === 'annual' && (
                  <div className="flex justify-between text-lg">
                    <span className="text-indigo-100">Pago anual único</span>
                    <span className="font-black text-2xl">{hasUnlockedResults ? totalPayment.toFixed(0) : '?????'}€</span>
                  </div>
                )}
              </div>
            </div>

            {/* What's included */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <h5 className="font-bold text-white mb-3">Incluido en cada licencia:</h5>
              <ul className="space-y-2">
                {[
                  'Acceso completo a la plataforma',
                  'Simulacros y exámenes ilimitados',
                  'Flashcards con repetición espaciada',
                  'Estadísticas y predicción ML',
                  includeAI ? 'Tutor IA 24/7 (GPT-4 + voz)' : null,
                  'PWA personalizada con tu marca',
                  'Soporte técnico prioritario',
                  'Actualizaciones de contenido'
                ].filter(Boolean).map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* ROI Preview */}
            <div className={`bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl p-4 border border-emerald-400/30 relative ${!hasUnlockedResults ? 'overflow-hidden' : ''}`}>
              {!hasUnlockedResults && <div className="absolute inset-0 backdrop-blur-sm bg-black/20" />}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-emerald-300 text-sm">ROI estimado</p>
                  <p className="text-white font-bold text-xl">
                    {hasUnlockedResults ? `+${roi}%` : '+???%'} 
                    <span className="text-sm font-normal text-emerald-300 ml-1">retorno anual</span>
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <Button 
              onClick={() => setShowLeadModal(true)}
              className="w-full py-6 rounded-xl bg-white text-indigo-600 hover:bg-slate-100 font-bold text-lg"
            >
              {hasUnlockedResults ? 'Solicitar demo personalizada' : 'Obtener presupuesto personalizado'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            <p className="text-center text-xs text-slate-500">
              Sin compromiso • Respuesta en 24h • Facturación flexible
            </p>
          </div>
        </div>
      </motion.div>
    </>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const features = [
    { 
      icon: Brain, 
      title: 'Tutor IA 24/7', 
      description: 'Tu profesor particular disponible siempre. Resuelve dudas, explica conceptos y te guía en tu preparación.',
      color: colors.blue
    },
    { 
      icon: Target, 
      title: 'Simulacros Reales', 
      description: 'Practica con exámenes que replican el formato oficial con puntuación OPE configurable.',
      color: colors.orange
    },
    { 
      icon: TrendingUp, 
      title: 'Predicción de Nota', 
      description: 'Machine Learning que calcula tu probabilidad de aprobar basándose en tu rendimiento.',
      color: colors.purple
    },
    { 
      icon: BookOpen, 
      title: 'Flashcards FSRS', 
      description: 'Memoriza con ciencia. Repetición espaciada que optimiza tu retención a largo plazo.',
      color: colors.pink
    },
    { 
      icon: Flame, 
      title: 'Rachas y XP', 
      description: 'Mantén la motivación con gamificación: rachas diarias, puntos de experiencia y logros.',
      color: colors.red
    },
    { 
      icon: Users, 
      title: 'Comunidad', 
      description: 'Conecta con otros opositores, comparte dudas y participa en retos grupales.',
      color: colors.primary
    }
  ];

  const categories = [
    { emoji: '🏥', name: 'Sanidad', description: 'Enfermería, Medicina, TCAE, Celador...', count: 16 },
    { emoji: '🏛️', name: 'Administración', description: 'Auxiliar, Administrativo, Correos...', count: 5 },
    { emoji: '🚔', name: 'Seguridad', description: 'Policía, Guardia Civil, Prisiones', count: 3 },
    { emoji: '⚖️', name: 'Justicia', description: 'Tramitación, Auxilio, Gestión', count: 3 },
    { emoji: '📚', name: 'Educación', description: 'Maestros, Profesores Secundaria', count: 3 }
  ];

  const faqs = [
    {
      q: '¿Cómo funciona la IA de TuPlazaIA?',
      a: 'Nuestra inteligencia artificial analiza tu rendimiento en tiempo real, adapta las preguntas a tu nivel, y te ofrece un tutor virtual que puede explicarte cualquier concepto del temario y resolver tus dudas al instante.'
    },
    {
      q: '¿El temario está actualizado?',
      a: 'Sí. Nuestro equipo de expertos actualiza el contenido según las últimas convocatorias y cambios legislativos. Recibirás notificaciones cuando haya actualizaciones.'
    },
    {
      q: '¿Puedo probar gratis?',
      a: '¡Sí! El plan Básico incluye 7 días de prueba gratuita con acceso completo. Se requiere tarjeta para activar la prueba, pero no se te cobrará hasta que termine el período de prueba.'
    },
    {
      q: '¿Puedo cancelar cuando quiera?',
      a: 'Por supuesto. No hay permanencia mínima. Cancela desde tu panel cuando quieras y mantén el acceso hasta fin del período pagado.'
    }
  ];

  const steps = [
    { 
      number: '1', 
      title: 'Elige tu oposición', 
      description: 'Selecciona entre +30 oposiciones en sanidad, administración, seguridad, justicia o educación.',
      color: colors.blue
    },
    { 
      number: '2', 
      title: 'Estudia con IA', 
      description: 'Tests adaptativos, tutor 24/7, flashcards científicas y simulacros que replican el examen real.',
      color: colors.purple
    },
    { 
      number: '3', 
      title: 'Aprueba tu plaza', 
      description: 'Conoce tu probabilidad de éxito, detecta puntos débiles y llega preparado al examen.',
      color: colors.primary
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between h-16 items-center">
            <OposaiLogo size={40} />
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#como-funciona" className="text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors">
                Cómo funciona
              </a>
              <a href="#oposiciones" className="text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors">
                Oposiciones
              </a>
              <a href="#funcionalidades" className="text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors">
                Funcionalidades
              </a>
              <a href="#academias" className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                Academias
              </a>
            </div>
            
            <div className="flex items-center gap-3">
              <LanguageSelector variant="nav" showFlag={true} showName={false} />
              <Button 
                variant="ghost" 
                onClick={() => navigate('/auth')}
                className="font-semibold text-gray-700 hover:text-gray-900"
              >
                Entrar
              </Button>
              <Button 
                onClick={() => navigate('/auth?mode=register')}
                className="rounded-xl px-5 font-bold shadow-md hover:shadow-lg transition-all"
                style={{ backgroundColor: colors.primary, color: 'white' }}
              >
                Empieza gratis
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-12 md:py-20 px-4 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left side - Text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold mb-6"
                style={{ backgroundColor: `${colors.primary}20`, color: colors.primaryDark }}
              >
                <Sparkles className="w-4 h-4" />
                La app #1 para opositar en España
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                <span style={{ color: colors.dark }}>Aprueba tu </span>
                <span style={{ color: colors.primary }}>oposición</span>
                <br />
                <span style={{ color: colors.dark }}>con </span>
                <span style={{ color: colors.blue }}>inteligencia artificial</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-lg">
                La plataforma más avanzada para funcionarios del Estado y personal estatutario. 
                Tests adaptativos, tutor IA 24/7 y predicción de nota con Machine Learning.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  onClick={() => navigate('/auth?mode=register')}
                  className="rounded-xl px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all"
                  style={{ backgroundColor: colors.primary, color: 'white' }}
                >
                  Empieza gratis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => document.getElementById('como-funciona').scrollIntoView({ behavior: 'smooth' })}
                  className="rounded-xl px-8 py-6 text-lg font-bold border-2 hover:bg-gray-50"
                  style={{ borderColor: colors.lightGray, color: colors.dark }}
                >
                  <Play className="w-5 h-5 mr-2" />
                  Ver cómo funciona
                </Button>
              </div>
              
              {/* Mini stats */}
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {['🧑‍⚕️', '👮', '👩‍⚖️'].map((emoji, i) => (
                      <div key={i} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center border-2 border-white">
                        {emoji}
                      </div>
                    ))}
                  </div>
                  <span className="text-gray-600 font-medium">+5.000 opositores</span>
                </div>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="w-4 h-4" style={{ color: colors.yellow, fill: colors.yellow }} />
                  ))}
                  <span className="text-gray-600 font-medium ml-1">4.9/5</span>
                </div>
              </div>
            </motion.div>
            
            {/* Right side - Mascot */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center lg:justify-end"
            >
              <div className="relative">
                <TuPlazaIAMascot size={280} />
                
                {/* Floating badges */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute -top-4 -left-4 bg-white rounded-xl p-3 shadow-lg border border-gray-100"
                >
                  <div className="flex items-center gap-2">
                    <Flame className="w-5 h-5" style={{ color: colors.orange }} />
                    <span className="font-bold text-gray-800">¡Racha de 7 días!</span>
                  </div>
                </motion.div>
                
                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                  className="absolute -bottom-2 -right-4 bg-white rounded-xl p-3 shadow-lg border border-gray-100"
                >
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5" style={{ color: colors.purple }} />
                    <span className="font-bold text-gray-800">+250 XP hoy</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-white border-y border-gray-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatCounter value="30+" label="Oposiciones" icon={BookOpen} color={colors.primary} />
            <StatCounter value="10K+" label="Preguntas" icon={Target} color={colors.blue} />
            <StatCounter value="24/7" label="Tutor IA" icon={Brain} color={colors.purple} />
            <StatCounter value="95%" label="Satisfacción" icon={Star} color={colors.orange} />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="como-funciona" className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4" style={{ color: colors.dark }}>
              Así de fácil es <span style={{ color: colors.primary }}>aprobar</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Solo 3 pasos te separan de tu plaza
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 h-full">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-extrabold text-white mb-4"
                    style={{ backgroundColor: step.color }}
                  >
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: colors.dark }}>{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
                
                {idx < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ChevronRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="oposiciones" className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4" style={{ color: colors.dark }}>
              Elige tu <span style={{ color: colors.blue }}>oposición</span>
            </h2>
            <p className="text-gray-600 text-lg">
              +30 oposiciones en 5 categorías profesionales
            </p>
          </div>
          
          <div className="grid gap-4">
            {categories.map((cat, idx) => (
              <CategoryCard key={idx} {...cat} delay={idx * 0.1} />
            ))}
          </div>
          
          <div className="mt-10 text-center">
            <Button 
              onClick={() => navigate('/auth?mode=register')}
              className="rounded-xl px-8 py-5 font-bold shadow-lg"
              style={{ backgroundColor: colors.primary, color: 'white' }}
            >
              Ver todas las oposiciones
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="funcionalidades" className="py-20 px-4" style={{ backgroundColor: `${colors.primary}08` }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4" style={{ color: colors.dark }}>
              Todo lo que necesitas para <span style={{ color: colors.primary }}>aprobar</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Funcionalidades premium diseñadas para maximizar tu preparación
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <FeatureCard key={idx} {...feature} delay={idx * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl p-8 md:p-12 text-center" style={{ background: colors.greenGradient }}>
            <div className="flex justify-center gap-1 mb-4">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-8 h-8 text-white" fill="white" />
              ))}
            </div>
            <blockquote className="text-xl md:text-2xl font-bold text-white mb-6 leading-relaxed">
              &ldquo;Desarrollado por profesionales que aprobaron sus oposiciones y conocen exactamente lo que necesitas para conseguir tu plaza.&rdquo;
            </blockquote>
            <div className="flex items-center justify-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-xl">
                🎓
              </div>
              <div className="text-left">
                <p className="font-bold text-white">Equipo TuPlazaIA</p>
                <p className="text-white/80 text-sm">Creado por opositores, para opositores</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4" style={{ color: colors.dark }}>
              Preguntas <span style={{ color: colors.blue }}>frecuentes</span>
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full p-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-bold text-gray-800">{faq.q}</span>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-400 transition-transform ${expandedFaq === idx ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence>
                  {expandedFaq === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5">
                        <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl p-8 md:p-16 text-center" style={{ background: colors.blueGradient }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6">
                Tu plaza te está esperando
              </h2>
              <p className="text-xl text-white/90 mb-10 max-w-xl mx-auto">
                Únete a miles de opositores que ya están preparando su futuro con TuPlazaIA
              </p>
              <Button
                onClick={() => navigate('/auth?mode=register')}
                className="rounded-xl px-10 py-7 text-xl font-bold shadow-2xl hover:scale-105 transition-all bg-white hover:bg-gray-50"
                style={{ color: colors.blue }}
              >
                Empieza gratis ahora
                <ArrowRight className="w-6 h-6 ml-2" />
              </Button>
              <p className="mt-6 text-white/70 text-sm">
                7 días gratis · Cancela cuando quieras
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* B2B Section - For Academies */}
      <section id="academias" className="py-24 px-4 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="px-4 py-2 rounded-full bg-indigo-500/20 text-indigo-300 text-sm font-medium mb-4 inline-block">
              Para Academias e Instituciones
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Tu Academia, Tu Marca
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Ofrece TuPlazaIA a tus alumnos con tu propia marca. 
              PWA white-label con tu dominio, colores y logo.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Feature 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-6">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Tu Dominio</h3>
              <p className="text-slate-400">
                Usa tu propio dominio (app.tuacademia.com) o un subdominio gratuito (tuacademia.tuplazaia.com).
                SSL incluido.
              </p>
            </motion.div>

            {/* Feature 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-6">
                <Smartphone className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">PWA Personalizada</h3>
              <p className="text-slate-400">
                Aplicación instalable en móvil con tu logo, colores y nombre.
                Tus alumnos la instalan desde el navegador.
              </p>
            </motion.div>

            {/* Feature 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-6">
                <BarChart3 className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Panel de Control</h3>
              <p className="text-slate-400">
                Dashboard institucional con estadísticas de alumnos, progreso, 
                riesgo de abandono y gestión de clases en vivo.
              </p>
            </motion.div>
          </div>

          {/* ROI Calculator */}
          <ROICalculator />
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <OposaiLogo size={36} className="[&_span]:text-white" />
            
            <div className="flex items-center gap-6 text-gray-400 text-sm">
              <a href="/aviso-legal" className="hover:text-white transition-colors">Aviso Legal</a>
              <a href="/privacidad" className="hover:text-white transition-colors">Privacidad</a>
              <a href="/pricing" className="hover:text-white transition-colors">Precios</a>
              <a href="/contacto" className="hover:text-white transition-colors">Contacto</a>
            </div>
            
            <p className="text-gray-400 text-sm">
              © 2025 TuPlazaIA. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
